import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ChevronRight, Package, PackageOpen, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import Disclaimer from "@/components/Disclaimer";
import { FpsBadge, PtaBadge } from "@/components/Badges";
import { fetchDevices } from "@/lib/api";
import { PRICE_INDEX_BANDS } from "@/data/static";

const pkr = (n) => `PKR ${n.toLocaleString("en-PK")}`;

export default function PriceIndex() {
  const [all, setAll] = useState([]);
  useEffect(() => { fetchDevices({ sort: "popularity" }).then(setAll).catch(() => {}); }, []);

  const bands = useMemo(
    () => PRICE_INDEX_BANDS.map((b) => ({ ...b, picks: all.filter(b.pred).sort((a, c) => c.popularity - a.popularity).slice(0, 4) })),
    [all]
  );

  const usedVsBox = useMemo(() => all.filter((d) => d.conditions.includes("Used Kit") && d.conditions.includes("Box Pack")).slice(0, 4), [all]);

  return (
    <>
      <Seo title="Price Index - Gaming Phone Prices in Pakistan" description="Quick market discovery: gaming phone price bands in PKR, top picks per band, and used-kit vs box-pack value comparison." path="/price-index" />

      <div className="border-b border-border bg-graphite">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-neon/30 bg-neon/10 px-3 py-1 text-xs font-heading font-semibold text-neon"><TrendingUp className="h-3.5 w-3.5" /> Market Overview</span>
          <h1 className="mt-4 font-display text-2xl font-black tracking-tight text-white sm:text-4xl">Price Index</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-300 sm:text-base">Live gaming phone prices in Pakistan by band. Use it for quick discovery, then dive into the FPS Finder.</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6"><Disclaimer /></div>

        <div className="space-y-6">
          {bands.map((b) => (
            <div key={b.label} data-testid={`price-band-${b.label}`} className="rounded-2xl border border-border bg-slateSurface p-5 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="font-heading text-lg font-bold text-white">{b.label}</h2>
                  <p className="text-xs text-slate-400">{b.note}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full border border-border bg-graphite px-3 py-1 text-xs text-slate-400">{b.picks.length} top picks</span>
                  <a
                    href={`https://phoneflip.pk/search?min_price=${b.min}&max_price=${b.max}`}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    data-testid={`phoneflip-band-${b.label.replace(/\s+/g, "-").toLowerCase()}`}
                    className="inline-flex items-center gap-1 rounded-full border border-cyber/30 bg-cyber/10 px-3 py-1 text-xs font-medium text-cyber hover:bg-cyber/20"
                  >
                    Live listings <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>

              {b.picks.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">No devices in this band yet.</p>
              ) : (
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {b.picks.map((d) => (
                    <Link key={d.slug} to={`/device/${d.slug}`} className="group flex gap-3 rounded-xl border border-border bg-graphite p-3 transition-all hover:border-neon/40">
                      <img src={d.image} alt={d.name} loading="lazy" className="h-16 w-16 shrink-0 rounded-lg object-cover" />
                      <div className="min-w-0">
                        <div className="truncate font-heading text-sm font-semibold text-white group-hover:text-neon">{d.name}</div>
                        <div className="mt-0.5 font-display text-xs font-bold text-neon">{pkr(d.price_min)}+</div>
                        <div className="mt-1"><FpsBadge fps={d.fps_tier} /></div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* used vs box */}
        <section className="mt-10">
          <h2 className="mb-4 font-heading text-xl font-bold text-white sm:text-2xl">Used Kit vs Box Pack</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-cyber/30 bg-slateSurface p-5">
              <div className="flex items-center gap-2 text-cyber"><PackageOpen className="h-5 w-5" /><h3 className="font-heading text-sm font-bold">Used Kit</h3></div>
              <ul className="mt-3 space-y-2 text-sm text-slate-300">
                <li>✓ Flagship gaming performance for less money</li>
                <li>✓ Best value per rupee for high FPS</li>
                <li>✗ Often non-PTA - verify status</li>
                <li>✗ Requires careful inspection (screen, battery, heat)</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-neon/30 bg-slateSurface p-5">
              <div className="flex items-center gap-2 text-neon"><Package className="h-5 w-5" /><h3 className="font-heading text-sm font-bold">Box Pack</h3></div>
              <ul className="mt-3 space-y-2 text-sm text-slate-300">
                <li>✓ Sealed, warranty & peace of mind</li>
                <li>✓ Usually PTA-approved with local support</li>
                <li>✗ Higher price for same chipset tier</li>
                <li>✗ Fewer ultra-premium options at low budgets</li>
              </ul>
            </div>
          </div>
          {usedVsBox.length > 0 && (
            <p className="mt-3 text-xs text-slate-500">Phones offered in both conditions: {usedVsBox.map((d) => d.name).join(", ")}.</p>
          )}
        </section>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/fps-finder" className="inline-flex items-center gap-1 rounded-lg bg-neon px-5 py-3 text-sm font-heading font-bold text-charcoal">Open FPS Finder <ChevronRight className="h-4 w-4" /></Link>
          <Link to="/category/best-gaming-kit-phones-pakistan" className="inline-flex items-center gap-1 rounded-lg border border-cyber/40 bg-cyber/10 px-5 py-3 text-sm font-heading font-bold text-cyber">Best Kit Phones <ChevronRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </>
  );
}
